import { useCallback, useEffect, useRef, useState } from "react";
import { GoGear } from "react-icons/go";
import Axios from "axios";
import _ from "lodash";
import DynamicExportMenu from "./DynamicExportMenu";
import "./DynamicExportStyle.scss";
import { apiRoutes } from "../../constants";
import { useToasts } from "react-toast-notifications";
import { parseError } from "../../helpers/utils";

const DynamicExport = ({
  className = "",
  buttonClass = "",
  type = "",
  apiType = "",
  permission = false,
  disabled = true,
  exportUrl,
  exportFilter,
  customFilterFormatter,
  suffix = "",
}) => {
  const { addToast } = useToasts();

  const [show, setShow] = useState(false);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [backUpCompleted, setBackUpCompleted] = useState(false);
  const [customExportMode, setCustomExportMode] = useState(false);
  const menuRef = useRef(null);

  const token = localStorage.getItem("access_token");
  const config = {
    headers: {
      Authorization: `JWT ${token}`,
    },
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
  };
  const handleShowSuccessModal = (message) => {
    setShowSuccessModal(true);
    setSuccessMessage(message);
  };

  const handleChange = (index) => {
    const newOptions = [...options];
    newOptions[index].isChecked = !newOptions[index].isChecked;
    setOptions(newOptions);
  };

  const getFromSessionStorage = () => {
    if (backUpCompleted) return;
    if (_.isEmpty(type)) return;
    if (_.isEmpty(options)) return;
    const data = sessionStorage.getItem("exportData");
    if (!_.isEmpty(data)) {
      const parsedData = JSON.parse(data);
      const backOptions = parsedData?.[type + suffix];
      if (!_.isEmpty(backOptions)) setOptions(backOptions);
    }
    setBackUpCompleted(true);
  };

  const setToSessionStorage = () => {
    if (!backUpCompleted) return;
    if (_.isEmpty(type)) return;
    if (_.isEmpty(options)) return;
    const localData = sessionStorage.getItem("exportData");
    const prevData = _.isEmpty(localData) ? {} : JSON.parse(localData);
    const data = {
      [type + suffix]: options,
    };
    sessionStorage.setItem(
      "exportData",
      JSON.stringify({ ...(prevData ?? {}), ...(data ?? {}) })
    );
  };

  const processOptions = (data) => {
    if (_.isEmpty(data)) return;
    const requiredFields = data?.requiredFields ?? [];
    const defaultFields = data?.defaultFields ?? [];
    const optionsData = _.toPairs(data?.fields ?? {})?.map(([key, value]) => {
      return {
        label: value?.label,
        value: value?.key,
        isRequired: _.includes(requiredFields, value?.key),
        isDefault: _.includes(defaultFields, value?.key),
        isChecked:
          _.includes(defaultFields, value?.key) ||
          _.includes(requiredFields, value?.key),
      };
    });
    setOptions(optionsData);
  };

  const getExportFields = () => {
    if (_.isEmpty(type)) return;
    Axios.get(apiRoutes.exportFields.replace("{type}", type), config)
      .then((response) => {
        processOptions(response?.data?.data?.attributes);
      })
      .catch((error) => {
        console.warn(error);
      });
  };

  const exportData = (url, fields) => {
    setIsLoading(true);
    Axios.post(
      url,
      {
        data: {
          type: apiType,
          attributes: {
            fields: fields,
          },
        },
      },
      config
    )
      .then((response) => {
        handleShowSuccessModal(response?.data?.data?.message);
      })
      .catch((error) => {
        addToast(parseError(error), {
          appearance: "error",
        });
      })
      .finally(() => setIsLoading(false));
  };

  const handleExport = () => {
    if (_.isEmpty(exportUrl)) return;
    const exportFields = options
      .filter((item) => item.isChecked)
      .map((item) => item.value);
    if (_.isEmpty(exportFields)) return;
    const url = new URL(exportUrl);
    const filter = customFilterFormatter
      ? customFilterFormatter(exportFilter)
      : exportFilter;
    if (!_.isEmpty(filter)) {
      url.search = filter;
    }
    exportData(url, exportFields);
  };

  const handleMenuClose = (event) => {
    if (
      event?.type === "resize" ||
      (menuRef?.current && !menuRef?.current?.contains(event?.target))
    ) {
      handleClose();
    }
  };

  useEffect(() => {
    if (!menuRef?.current || !show) return;
    document.addEventListener("mousedown", handleMenuClose);
    document.addEventListener("touchstart", handleMenuClose);
    window.addEventListener("resize", handleMenuClose);
    return () => {
      document.removeEventListener("mousedown", handleMenuClose);
      document.removeEventListener("touchstart", handleMenuClose);
      window.removeEventListener("resize", handleMenuClose);
    };
  }, [menuRef, show]);

  const checkForCustomExportMode = useCallback(() => {
    if (_.isEmpty(options)) {
      setCustomExportMode(false);
      return;
    }
    if (options.find((item) => item?.isChecked !== item?.isDefault)) {
      setCustomExportMode(true);
    } else {
      setCustomExportMode(false);
    }
  }, [options]);

  useEffect(() => {
    checkForCustomExportMode();
  }, [options]);

  useEffect(() => {
    getExportFields();
  }, [type]);

  useEffect(() => {
    getFromSessionStorage();
    setToSessionStorage();
  }, [type, options]);

  if (!permission) return null;
  return (
    <>
      <div className={`export-context-menu position-relative ${className}`}>
        <div className={`dynamic-export-wrapper position-relative`}>
          <button
            onClick={handleExport}
            className={`${buttonClass} dynamic-export-btn btn btn-secondary position-relative`}
            disabled={
              disabled ||
              _.isEmpty((options ?? []).filter((item) => item.isChecked))
            }
          >
            Export
            {isLoading && (
              <span className="button-loader-container">
                <div
                  className="spinner-border spinner-border-sm ml-1 text-white"
                  role="status"
                ></div>
              </span>
            )}
          </button>
          <button
            className={`dynamic-export-settings btn btn-secondary rounded-0`}
            onClick={handleShow}
            disabled={disabled}
          >
            <GoGear />
          </button>
          {customExportMode && (
            <span className="dynamic-export-settings-badge" />
          )}
        </div>
        <DynamicExportMenu
          ref={menuRef}
          isOpen={show}
          options={options}
          onChange={handleChange}
          onClose={handleClose}
        />
      </div>
      <div className={`reportStatusPopup ${showSuccessModal ? "show" : ""}`}>
        <div className="popUp">
          <div className="content">{successMessage}</div>
          <div className="action">
            <button
              className="btn btn-secondary"
              onClick={handleCloseSuccessModal}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DynamicExport;
